import { DateTimeObj, Do } from '../../../../universal';
import { React, _ } from '../../../lib';
import { reactFlyout } from '../../component-react';
import { svrtsRequest } from '../../svrts-interface';
import { FormButton, FormButtonSet } from '../buttons';
import { FormComponent } from '../form';
import { ListGrid } from '../list-grid';
import { LoadingSpinnerLarge } from '../loading';
import { useRSInstance } from '../meta-types';
import { TabControl } from '../tabs';
import { NoteAttachment } from './attachments';
import { FormFieldNoteBody, FormFieldNoteTitle } from './edit';
import { FormFieldNoteTags, getFlattenedTags } from './tags';
export const quickTidyNotes = (rs, notes) => {
    openTidyingFlyout({
        rs,
        availableNotes: notes,
        defaultSelectedNotes: notes.map(x => x.ID),
        autoStart: true,
    });
};
export const startTidyingNote = (rs) => {
    const availableNotes = rs.props.notes.filter(x => !x.Deleted && !x.Archived);
    const selectedNotes = Do(() => {
        if (rs.state.selectedNotes.length == 0) {
            return _.map(availableNotes, x => x.ID);
        }
        return rs.state.selectedNotes;
    });
    openTidyingFlyout({
        rs,
        availableNotes,
        defaultSelectedNotes: selectedNotes,
        autoStart: false,
    });
};
const openTidyingFlyout = (input) => {
    const cmpt = () => (React.createElement(TidyNotes, { rs: input.rs, availableNotes: input.availableNotes, defaultSelectedNotes: input.defaultSelectedNotes, autoStart: input.autoStart }));
    if (!input.rs.props.openFlyoutAsWindow) {
        reactFlyout('Tidy Notes', [1024, 600], cmpt());
        return;
    }
    input.rs.props.openFlyoutAsWindow(openTidyingFlyoutWindow, {
        rs: input.rs,
        availableNotes: input.availableNotes,
        defaultSelectedNotes: input.defaultSelectedNotes,
        autoStart: input.autoStart,
    });
};
const openTidyingFlyoutWindow = () => ({
    title: () => 'Tidy Notes',
    func: '',
    sid: null,
    size: [1024, 560],
    sizeMin: [720, 360],
    sizeMax: [1920, null],
    cmpt: TidyNotes,
    fields: ['rs', 'availableNotes', 'defaultSelectedNotes', 'autoStart'],
});
const TidyNotes = (props) => {
    const rs = props.rs;
    const notes = React.useMemo(() => _(props.availableNotes)
        .keyBy(x => x.ID)
        .value(), [props.availableNotes]);
    const tidyRS = useRSInstance({
        props: {
            availableNotes: props.availableNotes,
            defaultSelectedNotes: props.defaultSelectedNotes,
            autoStart: props.autoStart,
            flyout: props.flyout,
            window: props.window,
        },
        defaultState: React.useCallback((p) => ({
            selectedNotes: p.defaultSelectedNotes,
            loadingCount: 0,
            errorMsg: '',
            results: null,
            currentApprovalNote: null,
            selectedApprovalTab: 'old-text',
            editedNewNote: null,
            resolvedResults: [],
            resolvedApprovals: [],
        }), []),
        refs: {},
        actionToDelta: null,
    });
    const hasAutoStarted = React.useRef(false);
    const onScan = React.useCallback(() => {
        tidyRS.updateState({
            loadingCount: tidyRS.state.selectedNotes.length,
            errorMsg: null,
            results: null,
        });
        svrtsRequest({
            data: {
                funcID: [0, 62],
                notes: _(tidyRS.state.selectedNotes)
                    .map(x => {
                    const note = notes[x];
                    if (!note) {
                        return null;
                    }
                    return {
                        ID: note.ID,
                        Title: note.Title || null,
                        Note: note.Note || null,
                        Attachment: note.Attachment?.Checksum ?? null,
                        Tags: note.Tags ?? [],
                    };
                })
                    .compact()
                    .value(),
                tags: _(getFlattenedTags(rs))
                    .sortBy(x => x.DisplayName)
                    .filter(x => !(x.NonEditable ?? false))
                    .filter(x => !(x.BanFromGenerativePick ?? false))
                    .map(x => ({
                    ID: x.ID,
                    DisplayName: x.DisplayName,
                    Description: x.Description,
                }))
                    .value(),
                summaryContextPrompt: rs.props.summaryContextPrompt ?? null,
            },
            any: () => {
                tidyRS.updateState({ loadingCount: 0 });
            },
            no: data => {
                tidyRS.updateState({ errorMsg: data.message });
            },
            yes: data => {
                const newNote = data.results[0];
                tidyRS.updateState({
                    results: data.results.map(r => ({
                        ...r,
                        Title: r.Title ?? '',
                    })),
                    currentApprovalNote: newNote?.ID ?? null,
                    editedNewNote: newNote
                        ? {
                            title: newNote.Title ?? '',
                            note: newNote.Note ?? '',
                            tags: newNote.Tags ?? [],
                        }
                        : null,
                });
            },
        });
    }, [notes, rs, tidyRS]);
    React.useEffect(() => {
        if (props.autoStart && !hasAutoStarted.current) {
            hasAutoStarted.current = true;
            onScan();
        }
    }, [props.autoStart, onScan]);
    const wrap = (children) => (React.createElement("div", { className: "tailwind-wrapper", style: { height: '100%' } }, children));
    if (tidyRS.state.results != null) {
        return wrap(React.createElement(TidyNoteApproval, { rsNotes: rs, rsTidy: tidyRS }));
    }
    if (tidyRS.props.autoStart) {
        return wrap(React.createElement(LoadingSpinnerLarge, null));
    }
    return wrap(React.createElement(TidyNoteSelection, { rsTidy: tidyRS, onScanStart: onScan }));
};
const TidyNoteSelection = (props) => {
    const rsTidy = props.rsTidy;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "pb-2 text-left" }, "Select which notes you want to tidy. The system will analyse the content of the note (including any attachment) and suggest a better title/description for you to approve, translate any non-English content, and add any missing tags."),
        React.createElement("div", { className: "h-[calc(100%-120px)]" },
            React.createElement(ListGrid, { data: _.values(rsTidy.props.availableNotes), pk: x => x.ID, value: rsTidy.state.selectedNotes, onUpdate: v => {
                    rsTidy.updateState({ selectedNotes: v });
                }, checkboxSelection: true, multiple: true, multipleDefault: true, readOnly: rsTidy.state.loadingCount > 0, defaultSortingKey: "edited", defaultSortingReverse: true, fields: [
                    {
                        key: 'edited',
                        lbl: 'Edited',
                        width: { init: 150 },
                        text: x => DateTimeObj.parse(x.LastEdited).fmtDMY({
                            seconds: false,
                            shortYear: true,
                            weekday: true,
                        }),
                        sortVal: x => x.LastEdited,
                    },
                    {
                        key: 'name',
                        lbl: 'Note',
                        text: x => x.Title || x.Note || '[no content]',
                    },
                    {
                        key: 'progress',
                        lbl: 'Status',
                        width: { init: 100 },
                        text: x => {
                            if (rsTidy.state.results != null) {
                                return 'Completed';
                            }
                            if (rsTidy.state.loadingCount > 0 &&
                                rsTidy.state.selectedNotes.includes(x.ID)) {
                                return 'Scanning';
                            }
                            if (rsTidy.state.loadingCount > 0 &&
                                !rsTidy.state.selectedNotes.includes(x.ID)) {
                                return '';
                            }
                            if (rsTidy.state.selectedNotes.includes(x.ID)) {
                                return 'Selected';
                            }
                            return '';
                        },
                    },
                ] })),
        React.createElement(FormButtonSet, { loading: rsTidy.state.loadingCount > 0, msg: rsTidy.state.errorMsg ?? '' },
            React.createElement(FormButton, { lbl: "Scan Notes", type: "submit", disabled: rsTidy.state.selectedNotes.length == 0, title: rsTidy.state.selectedNotes.length == 0
                    ? 'No notes selected'
                    : undefined, onClick: () => {
                    props.onScanStart();
                } }),
            React.createElement(FormButton, { lbl: "Cancel", onClick: () => {
                    rsTidy.props.flyout?.Close();
                    rsTidy.props.window?.Close();
                } }))));
};
const TidyNoteApproval = (props) => {
    const rsTidy = props.rsTidy;
    const currentNote = rsTidy.state.currentApprovalNote
        ? rsTidy.props.availableNotes.find(x => x.ID == rsTidy.state.currentApprovalNote)
        : null;
    const nextNote = () => {
        const resolved = [
            ...rsTidy.state.resolvedResults,
            rsTidy.state.currentApprovalNote,
        ].filter(x => x != null);
        const newNote = (rsTidy.state.results ?? []).find(x => !resolved.includes(x.ID));
        if (!newNote) {
            rsTidy.props.flyout?.Close();
            rsTidy.props.window?.Close();
        }
        rsTidy.updateState({
            resolvedResults: resolved,
            currentApprovalNote: newNote?.ID ?? null,
            editedNewNote: newNote
                ? {
                    title: newNote.Title ?? '',
                    note: newNote.Note ?? '',
                    tags: newNote.Tags ?? [],
                }
                : null,
        });
    };
    const getDefault = () => ({
        title: currentNote?.Title ?? '',
        note: currentNote?.Note ?? '',
        tags: currentNote?.Tags ?? [],
    });
    const currentNoteID = currentNote?.ID ?? -1;
    React.useEffect(() => {
        const results = rsTidy.state.results;
        const resolved = rsTidy.state.resolvedResults;
        const unresolved = results?.filter(x => !resolved.includes(x.ID));
        if (unresolved == null || unresolved.length == 0) {
            rsTidy.props.flyout?.Close();
            rsTidy.props.window?.Close();
        }
    }, [rsTidy, currentNoteID]);
    return (React.createElement("div", { className: "h-full" },
        React.createElement("div", { className: "flex h-[calc(100%-72px)]" },
            React.createElement("div", { className: "flex-1 max-w-1/2" },
                React.createElement(TabControl, { value: rsTidy.state.selectedApprovalTab, onUpdate: v => {
                        rsTidy.updateState({ selectedApprovalTab: v });
                    }, tabs: [
                        {
                            value: 'old-text',
                            label: 'Text',
                            content: () => (React.createElement(FormComponent, { key: props.rsTidy.state.currentApprovalNote, fields: {
                                    title: FormFieldNoteTitle(),
                                    tags: FormFieldNoteTags(props.rsNotes, {
                                        generativeOnly: true,
                                    }),
                                    note: FormFieldNoteBody(props.rsNotes, 16),
                                }, formPrompt: "Editing a note", lblWidth: 40, disabled: true, defaultValue: getDefault, excludeButtons: true, disableFormFill: true, value: null, onUpdate: () => { }, onSave: () => { } })),
                        },
                        {
                            value: 'attachment',
                            label: 'Attachment',
                            content: () => (React.createElement(NoteAttachment, { key: currentNote?.Attachment?.Checksum ??
                                    'attachment', attachment: currentNote?.Attachment ?? null })),
                        },
                    ] })),
            React.createElement("div", { className: "flex-1 pt-8" },
                React.createElement(FormComponent, { key: props.rsTidy.state.currentApprovalNote, fields: {
                        title: FormFieldNoteTitle(),
                        tags: FormFieldNoteTags(props.rsNotes, {
                            generativeOnly: true,
                        }),
                        note: FormFieldNoteBody(props.rsNotes, 16),
                    }, formPrompt: "Editing a note", lblWidth: 40, defaultValue: getDefault, value: rsTidy.state.editedNewNote, onUpdate: v => {
                        rsTidy.updateState({ editedNewNote: v });
                    }, excludeButtons: true, disableFormFill: true, onSave: () => { } }))),
        React.createElement(FormButtonSet, { loading: false, msg: rsTidy.state.errorMsg ?? '' },
            React.createElement(FormButton, { lbl: "Save", onClick: () => {
                    rsTidy.updateState(s => ({ loadingCount: s.loadingCount + 1 }));
                    makeNoteChanges({
                        rsNotes: props.rsNotes,
                        rsTidy: rsTidy,
                        noteID: currentNote?.ID ?? '',
                        noteOld: {
                            title: currentNote?.Title ?? '',
                            bodyText: currentNote?.Note ?? '',
                            tags: currentNote?.Tags ?? [],
                        },
                        newNote: {
                            title: rsTidy.state.editedNewNote?.title ?? '',
                            bodyText: rsTidy.state.editedNewNote?.note ?? '',
                            tags: rsTidy.state.editedNewNote?.tags ?? [],
                        },
                        onComplete: successOrError => {
                            rsTidy.updateState(s => ({
                                loadingCount: s.loadingCount - 1,
                            }));
                            if (successOrError === true) {
                                rsTidy.updateState(s => ({
                                    resolvedApprovals: _.compact([
                                        ...s.resolvedApprovals,
                                        currentNote?.ID ?? null,
                                    ]),
                                }));
                            }
                            else {
                                rsTidy.updateState({ errorMsg: successOrError });
                            }
                        },
                    }).catch(err => {
                        console.error(err);
                    });
                    nextNote();
                } }),
            React.createElement(FormButton, { lbl: "Skip", type: "standard", onClick: () => {
                    nextNote();
                } }),
            React.createElement(FormButton, { lbl: "Close", type: "standard", onClick: () => {
                    rsTidy.props.flyout?.Close();
                    rsTidy.props.window?.Close();
                } }))));
};
const makeNoteChanges = async (input) => {
    const updateNote = async () => new Promise(resolve => {
        input.rsNotes.props.editing?.onEdit({
            noteID: input.noteID,
            changes: {
                title: input.newNote.title,
                note: input.newNote.bodyText,
            },
            onComplete: successOrError => {
                resolve(successOrError);
            },
        });
    });
    const updateTags = async () => {
        const additions = input.newNote.tags.filter(x => !input.noteOld.tags.includes(x));
        const deletions = input.noteOld.tags.filter(x => !input.newNote.tags.includes(x));
        return new Promise(resolve => {
            if (!input.rsNotes.props.tagging?.onUpdate) {
                resolve(true);
                return;
            }
            input.rsNotes.props.tagging.onUpdate({
                additions: additions.map(x => ({
                    noteID: input.noteID,
                    tagID: x,
                })),
                deletions: deletions.map(x => ({
                    noteID: input.noteID,
                    tagID: x,
                })),
                onComplete: successOrError => {
                    resolve(successOrError);
                },
            });
        });
    };
    const result = await updateNote();
    if (result !== true) {
        input.onComplete(result);
        return;
    }
    input.onComplete(await updateTags());
};
//# sourceMappingURL=generative-tidy.js.map